import type { CanvasObject } from "@refinement-canvas/domain";
import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "./canvas-coordinates";

export const THUMBNAIL_WIDTH = 320;
export const THUMBNAIL_HEIGHT = Math.round((CANVAS_HEIGHT_PX / CANVAS_WIDTH_PX) * THUMBNAIL_WIDTH);

export function renderCanvasThumbnailDataUrl(objects: readonly CanvasObject[]): string {
  const canvas = document.createElement("canvas");
  canvas.width = THUMBNAIL_WIDTH;
  canvas.height = THUMBNAIL_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  const scaleX = THUMBNAIL_WIDTH / CANVAS_WIDTH_PX;
  const scaleY = THUMBNAIL_HEIGHT / CANVAS_HEIGHT_PX;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  drawGrid(ctx);

  const ordered = objects.slice().sort((a, b) => a.zOrder - b.zOrder);
  for (const object of ordered) {
    ctx.save();
    ctx.translate((object.x + object.w / 2) * scaleX, (object.y + object.h / 2) * scaleY);
    ctx.rotate(((object.rotation ?? 0) * Math.PI) / 180);
    ctx.translate((-object.w / 2) * scaleX, (-object.h / 2) * scaleY);
    drawObject(ctx, object, scaleX, scaleY);
    ctx.restore();
  }

  return canvas.toDataURL("image/png");
}

export function dataUrlToPngBlob(dataUrl: string): Blob | null {
  const [header, body] = dataUrl.split(",");
  if (!header?.startsWith("data:image/png;base64") || !body) return null;
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: "image/png" });
}

function drawGrid(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = "rgba(26, 26, 46, 0.08)";
  for (let x = 0; x < THUMBNAIL_WIDTH; x += 24) {
    for (let y = 0; y < THUMBNAIL_HEIGHT; y += 24) {
      ctx.fillRect(x, y, 1, 1);
    }
  }
}

function drawObject(
  ctx: CanvasRenderingContext2D,
  object: CanvasObject,
  scaleX: number,
  scaleY: number
): void {
  const w = Math.max(1, object.w * scaleX);
  const h = Math.max(1, object.h * scaleY);
  switch (object.type) {
    case "sticky":
      ctx.fillStyle = object.color ?? "#fff5b3";
      ctx.strokeStyle = "rgba(30, 41, 59, 0.2)";
      ctx.lineWidth = 1;
      ctx.fillRect(0, 0, w, h);
      ctx.strokeRect(0, 0, w, h);
      return;
    case "shape":
      ctx.strokeStyle = object.stroke ?? "#1a1a2e";
      ctx.fillStyle = object.fill ?? "rgba(255,255,255,0)";
      ctx.lineWidth = 1.5;
      drawShape(ctx, object.shapeKind, w, h);
      return;
    case "text": {
      ctx.fillStyle = object.color ?? "#1a1a2e";
      ctx.font = `${Math.max(6, (object.fontSize ?? 16) * scaleY)}px sans-serif`;
      // 文字寄せ（未指定は左寄せ）を反映し、画面表示・PDF と見た目を揃える。
      const align = object.textAlign ?? "left";
      ctx.textAlign = align;
      const anchorX = align === "center" ? w / 2 : align === "right" ? w : 0;
      ctx.fillText(object.text.slice(0, 24), anchorX, Math.max(8, 14 * scaleY));
      ctx.textAlign = "left";
      return;
    }
    case "image":
    case "video":
      drawMediaPlaceholder(ctx, w, h, object.type === "video" ? "#111827" : "#dbeafe");
      return;
    case "link-card":
      drawMediaPlaceholder(ctx, w, h, "#eef2ff");
      ctx.fillStyle = "#1e3a8a";
      ctx.font = "7px sans-serif";
      ctx.fillText(object.title.slice(0, 24), 4, Math.min(h - 4, 12));
      return;
    case "pen-stroke":
      ctx.strokeStyle = object.color;
      ctx.lineWidth = Math.max(1, object.thickness * scaleX);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      object.points.forEach((point, index) => {
        const x = (point.x - object.x) * scaleX;
        const y = (point.y - object.y) * scaleY;
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      return;
    case "stamp": {
      // サムネイルでもスタンプ位置が分かるよう、絵文字グリフを中央に描画する。
      const glyph = STAMP_THUMB_GLYPH[object.stampKind];
      const sizePx = Math.max(6, Math.min(w, h) * 0.85);
      ctx.fillStyle = "#1a1a2e";
      ctx.font = `${sizePx}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(glyph, w / 2, h / 2);
      ctx.textAlign = "start";
      ctx.textBaseline = "alphabetic";
      return;
    }
  }
}

// サムネイル描画用のスタンプ種別 → 絵文字グリフ対応表。
// stamp-object.tsx の STAMP_KIND_GLYPH と同じ値を持つが、循環参照回避のため複製している。
const STAMP_THUMB_GLYPH: Record<"thumbs-up" | "fire" | "smile" | "hundred" | "plus", string> = {
  "thumbs-up": "👍",
  fire: "🔥",
  smile: "😊",
  hundred: "💯",
  plus: "➕",
};

function drawShape(
  ctx: CanvasRenderingContext2D,
  shapeKind: "arrow" | "circle" | "rectangle",
  w: number,
  h: number
): void {
  if (shapeKind === "circle") {
    ctx.beginPath();
    ctx.ellipse(w / 2, h / 2, Math.max(1, w / 2 - 1), Math.max(1, h / 2 - 1), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    return;
  }
  if (shapeKind === "arrow") {
    ctx.beginPath();
    ctx.moveTo(2, h / 2);
    ctx.lineTo(w - 6, h / 2);
    ctx.moveTo(w - 8, h / 2 - 4);
    ctx.lineTo(w - 2, h / 2);
    ctx.lineTo(w - 8, h / 2 + 4);
    ctx.stroke();
    return;
  }
  ctx.fillRect(0, 0, w, h);
  ctx.strokeRect(0, 0, w, h);
}

function drawMediaPlaceholder(ctx: CanvasRenderingContext2D, w: number, h: number, color: string): void {
  ctx.fillStyle = color;
  ctx.strokeStyle = "rgba(30, 41, 59, 0.2)";
  ctx.lineWidth = 1;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeRect(0, 0, w, h);
}
